// domain/model/VersionTimeline.js
import { addDays, toAsOfDate, toDateStr } from '../services/effectiveDating';

/**
 * VersionTimeline — every version of one effective-dated record, and the rules for adding another.
 *
 * A VALUE OBJECT over a series: no identity of its own, immutable, and reconstructed from the
 * stored versions on every read. The series it describes has identity (a funding source), the
 * timeline does not — two funders whose config changed on the same dates hold equal timelines and
 * nothing needs to tell them apart.
 *
 * Named for the SHAPE, not the holder, on the same terms as EffectivePeriod. Funding-source config
 * is its first holder; fee schedules and authorizations version identically, and when the second
 * one arrives this moves to a shared kernel as a folder move rather than a rewrite. Nothing in here
 * knows what a funding source is.
 *
 * WHY A TYPE RATHER THAN A COMPOSABLE. Every rule below is a pure function of the stored versions
 * and a date, and every one of them has a second caller: the dialog disables an option with it, and
 * the write path re-validates with it against a freshly-read timeline. Deriving the same rule twice
 * is how a disabled button and an accepted write come to disagree, and the disabled button is the
 * one the user trusts.
 *
 * NOTHING IS DENORMALIZED, and that is the point. "Which version is current" and "is there a
 * scheduled one" are answers that change at midnight with nobody writing anything, so they cannot
 * be stored — the morning after, any stored answer is wrong. They are comparisons against an `asOf`
 * the caller supplies, which is also what makes them testable without freezing a clock.
 *
 * TOMBSTONES ARE RETAINED, deliberately. A version that has never been in force may be soft-deleted
 * (see the collection's rules), and resolution excludes those — but `nextVersionAt` counts them,
 * because the document id encodes the version number. Numbering past a tombstone is what stops a
 * re-used effective date from aiming at an id that is already occupied.
 *
 * RESOLUTION IS TOTAL, over any list of versions including one that should not exist. At-most-one
 * scheduled version is an expectation of the editing UI, not a structural invariant: two managers
 * saving concurrently can produce two, and no client-side check prevents it. So `scheduledAsOf`
 * returns a list rather than asserting a single one — a model that claims an invariant storage
 * cannot hold is a model that lies. Billing is unaffected either way, because `currentAsOf` is
 * well-defined whatever the timeline holds.
 *
 * A version's effective PERIOD — running until the day before the next version's date — is
 * derivable from two adjacent entries and deliberately not materialized here. Nothing needs it yet;
 * the projector resolves a single date rather than a range.
 *
 * @domainObject value-object
 * @invariant Versions are ordered by effective date, then by version number within a date.
 * @invariant Version numbers within one effective date are unique and never re-used, tombstones
 *   included, because the document id is built from them.
 * @invariant Frozen — a timeline is rebuilt from storage, never edited in place.
 * @typedef {Object} ConfigVersion
 * @property {string} versionId
 * @property {string} effectiveFrom - 'YYYY-MM-DD'; the day this version takes over
 * @property {number} version - 1-based, unique within its effective date
 * @property {boolean} isDeleted
 *
 * @typedef {Object} VersionTimeline
 * @property {ConfigVersion[]} versions - ALL versions including tombstones, in order
 */

/** Ascending by effective date, then by version within the date. */
const inOrder = (a, b) => {
  const byDate = `${a.effectiveFrom}`.localeCompare(`${b.effectiveFrom}`);
  return byDate === 0 ? a.version - b.version : byDate;
};

/**
 * Builds a timeline from the stored versions of one record, in any order.
 *
 * A version whose `effectiveFrom` cannot be read as a calendar date is DROPPED rather than kept in
 * an unknown position. Keeping it would be worse than losing it: it sorts against every real date
 * lexicographically, so one malformed value silently decides which config is current. The
 * collection's rules require the field on create, so this is a guard against a hand-edited or
 * migrated document rather than an ordinary state.
 *
 * @param {Array<Object>} versions
 * @returns {VersionTimeline} frozen — a value object has no mutators
 */
export function createVersionTimeline(versions) {
  const readable = (versions ?? [])
    .filter((version) => toDateStr(version?.effectiveFrom) !== null)
    .map((version) => Object.freeze({
      versionId: version.versionId,
      effectiveFrom: toDateStr(version.effectiveFrom),
      version: Number(version.version) || 1,
      isDeleted: version.isDeleted === true,
    }));

  return Object.freeze({ versions: Object.freeze(readable.sort(inOrder)) });
} // createVersionTimeline()

/** The versions that resolve — tombstones excluded. */
const live = (timeline) => timeline.versions.filter((version) => version.isDeleted !== true);

/**
 * The version in force on the given date, or null when none has taken effect yet.
 *
 * Latest effective date at or before `asOf`, and the highest version number within that date —
 * which is what the ordering already gives, so this is the last candidate rather than a search.
 *
 * @param {VersionTimeline} timeline
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {ConfigVersion|null}
 */
export function currentAsOf(timeline, asOf) {
  const asOfDate = toAsOfDate(asOf);

  if (asOfDate === null) {
    return null;
  }
  const inForce = live(timeline).filter((version) => version.effectiveFrom <= asOfDate);

  return inForce.length === 0 ? null : inForce[inForce.length - 1];
} // currentAsOf()

/**
 * Versions that have not taken effect yet, soonest first.
 *
 * A LIST, not a single value — see the note on total resolution above. The editing UI offers the
 * first one and expects there to be at most one; it must not break when there are two.
 *
 * @param {VersionTimeline} timeline
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {ConfigVersion[]}
 */
export function scheduledAsOf(timeline, asOf) {
  const asOfDate = toAsOfDate(asOf);

  if (asOfDate === null) {
    return [];
  }
  return live(timeline).filter((version) => version.effectiveFrom > asOfDate);
} // scheduledAsOf()

/**
 * The live versions sharing one effective date, in version order.
 *
 * What the dialog's version indicator counts: more than one means the user is looking at an
 * amendment of that date rather than its original entry.
 *
 * READS ITS DATE, because the stored side of this comparison is already normalized —
 * `createVersionTimeline` saw to that — and comparing a raw argument against it with `===` matches
 * nothing at all rather than matching loosely. An unreadable date holds no versions.
 *
 * @param {VersionTimeline} timeline
 * @param {string} effectiveFrom - 'YYYY-MM-DD'
 * @returns {ConfigVersion[]}
 */
export function versionsOnDate(timeline, effectiveFrom) {
  const dateStr = toDateStr(effectiveFrom);

  if (dateStr === null) {
    return [];
  }
  return live(timeline).filter((version) => version.effectiveFrom === dateStr);
} // versionsOnDate()

/**
 * The version number a new entry at this effective date must take.
 *
 * COUNTS TOMBSTONES, unlike everything else here, and that is the whole reason it is not derived
 * from `versionsOnDate`. The document id is `{recordId}_{effectiveFrom}_{version}`, so re-using a
 * number aims a create at a document that still exists as a tombstone — which the collection's
 * rules classify as an update and refuse.
 *
 * READS ITS DATE FOR THE SAME REASON, and it matters most here. A raw argument compared against
 * normalized stored dates matches nothing, so this would answer 1 for a date that already holds a
 * version 1 — and 1 is the number that goes into the document id. `appendFundingSourceVersion`
 * normalizes before building that id and the create is refused, so the mistake surfaces as a
 * collision rather than a bad write; being correct because a guard one layer down catches it is not
 * the same as being correct.
 *
 * An unreadable date still answers 1. Nothing can act on it: `canTakeEffectAt` refuses the save
 * before the number is ever asked for, which is the only caller that writes.
 *
 * @param {VersionTimeline} timeline
 * @param {string} effectiveFrom - 'YYYY-MM-DD'
 * @returns {number}
 */
export function nextVersionAt(timeline, effectiveFrom) {
  const dateStr = toDateStr(effectiveFrom);
  const onDate = timeline.versions.filter((version) => version.effectiveFrom === dateStr);

  return onDate.reduce((highest, version) => Math.max(highest, version.version), 0) + 1;
} // nextVersionAt()

/**
 * The earliest date a version may take effect on a date it does not already occupy.
 *
 * The day after the version currently in force. Null when nothing is in force yet, which means
 * there is no floor rather than that every date is refused — so a caller using this to gate a save
 * must refuse an unreadable `asOf` itself, since that also lands on null. `canTakeEffectAt` does.
 *
 * NOT A FLOOR AT TODAY, deliberately: the date currently in force can be months old, so this
 * permits backdating into a window the projector has already run over. That is the specified
 * behavior — a mis-entered payer has to be correctable from the date it was wrong — and the
 * consequence is that amending a past date re-prices billing items already emitted for it. Showing
 * that blast radius is a later bet; this rule is the guardrail, not a defense.
 *
 * @param {VersionTimeline} timeline
 * @param {string} asOf - 'YYYY-MM-DD'
 * @returns {string|null} 'YYYY-MM-DD'
 */
export function minEffectiveFrom(timeline, asOf) {
  const current = currentAsOf(timeline, asOf);

  return current === null ? null : addDays(current.effectiveFrom, 1);
} // minEffectiveFrom()

/**
 * May a new version take effect on this date?
 *
 * TWO WAYS TO PASS, and the second is what makes the editor's three save options one operation. A
 * date that ALREADY holds versions is always amendable — adding v2 to the date currently in force
 * is the "overwrite existing" option, and it sits below the floor by definition. A date that holds
 * none is new, and must fall on or after the floor.
 *
 * This is also why there is no save-mode enum. "Amend this version", "take effect today" and "take
 * effect on a date" differ only in which date they choose, and one of them collapses into another
 * whenever the version in force started today — two affordances, one outcome. Modeling them as
 * distinct modes would give the write path a branch that is not a branch, and would make that
 * collapse a special case instead of a consequence.
 *
 * @param {VersionTimeline} timeline
 * @param {string} asOf - 'YYYY-MM-DD'
 * @param {string} effectiveFrom - 'YYYY-MM-DD' the candidate date
 * @returns {boolean}
 */
export function canTakeEffectAt(timeline, asOf, effectiveFrom) {
  const dateStr = toDateStr(effectiveFrom);

  // REFUSED BEFORE ANYTHING ELSE, and this one does NOT fall out of the guards below it. A null
  // floor means "nothing in force yet, so no floor" and is deliberately permissive — so an
  // unreadable as-of reaching `minEffectiveFrom` would come back null and REMOVE the backdating
  // guard rather than trip it. The one place in this module where failing closed has to be written.
  if (dateStr === null || toAsOfDate(asOf) === null) {
    return false;
  }
  if (versionsOnDate(timeline, dateStr).length > 0) {
    return true;
  }
  const floor = minEffectiveFrom(timeline, asOf);

  return floor === null || dateStr >= floor;
} // canTakeEffectAt()

/**
 * Which scheduled versions a save at this date would replace.
 *
 * Every scheduled version on a DIFFERENT date. Saving onto the scheduled version's own date is an
 * amendment of it and replaces nothing.
 *
 * DELIBERATELY INDIFFERENT TO WHICH SAVE OPTION WAS TAKEN, which has a consequence worth naming:
 * amending the version in force while a change is scheduled for next month cancels that change.
 * That is specified — a save re-states the config, and leaving a scheduled version behind to
 * override it weeks later is the more surprising outcome — and it is why the editor shows a warning
 * before a save that returns anything from here.
 *
 * @param {VersionTimeline} timeline
 * @param {string} asOf - 'YYYY-MM-DD'
 * @param {string} effectiveFrom - 'YYYY-MM-DD' the date being saved to
 * @returns {ConfigVersion[]}
 */
export function supersededBySaveAt(timeline, asOf, effectiveFrom) {
  return scheduledAsOf(timeline, asOf)
    .filter((version) => version.effectiveFrom !== toDateStr(effectiveFrom));
} // supersededBySaveAt()
